/**
 * ErrorHandler - Centralized error handling and logging
 */

export class ErrorHandler {
  constructor() {
    this.errorLog = [];
    this.maxLogSize = 100;
    this.errorCounts = {};
    // Start with offline mode enabled to prevent early network error dialogs
    this.isOfflineMode = true;
    this.setupGlobalErrorHandling();
  }

  /**
   * Set offline mode status
   */
  setOfflineMode(isOffline) {
    this.isOfflineMode = isOffline;
    console.log(`ErrorHandler: Offline mode ${isOffline ? 'enabled' : 'disabled'}`);
  }

  /**
   * Set up global error handling
   */
  setupGlobalErrorHandling() {
    // Handle uncaught errors
    window.addEventListener('error', (event) => {
      this.handleError(event.error || new Error(event.message), 'Uncaught Error', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(event.reason, 'Unhandled Promise Rejection');
      event.preventDefault(); // Prevent default browser console error
    });

    // Handle network errors
    window.addEventListener('offline', () => {
      this.handleError(new Error('Network connection lost'), 'Network Error');
    });
  }

  /**
   * Handle and log errors
   */
  handleError(error, context = 'Unknown', metadata = {}) {
    const errorInfo = {
      id: this.generateErrorId(),
      timestamp: new Date().toISOString(),
      message: error.message || 'Unknown error',
      stack: error.stack,
      context,
      metadata,
      userAgent: navigator.userAgent,
      url: window.location.href,
      severity: this.determineSeverity(error, context)
    };

    // Log to console for debugging
    console.group(`🚨 Error [${errorInfo.severity}]: ${errorInfo.context}`);
    console.error('Message:', errorInfo.message);
    console.error('Stack:', errorInfo.stack);
    console.error('Metadata:', errorInfo.metadata);
    console.groupEnd();

    // Add to error log
    this.addToLog(errorInfo);

    // Update error counts
    this.updateErrorCounts(errorInfo);

    // In production, avoid blocking dialogs; rely on non-blocking UI notices or logs
    // Disable modal error dialog to prevent interrupting users.
    // this.showUserError(errorInfo);

    // Report critical errors
    if (errorInfo.severity === 'critical') {
      this.reportCriticalError(errorInfo);
    }

    return errorInfo;
  }

  /**
   * Generate unique error ID
   */
  generateErrorId() {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Determine error severity
   */
  determineSeverity(error, context) {
    const message = error.message?.toLowerCase() || '';
    const contextLower = context.toLowerCase();

    // In offline mode, treat network/API errors as low severity since they're expected
    if (this.isOfflineMode && (
      contextLower.includes('network') ||
      contextLower.includes('api') ||
      message.includes('failed to fetch') ||
      message.includes('net::err_name_not_resolved') ||
      message.includes('network error') ||
      contextLower.includes('offline')
    )) {
      return 'low';
    }

    // Critical errors
    if (contextLower.includes('initialization') ||
      contextLower.includes('critical') ||
      message.includes('cannot read property') ||
      message.includes('is not defined')) {
      return 'critical';
    }

    // High severity errors
    if (contextLower.includes('network') ||
      contextLower.includes('load') ||
      contextLower.includes('api') ||
      message.includes('failed to fetch')) {
      return 'high';
    }

    // Medium severity errors
    if (contextLower.includes('ui') ||
      contextLower.includes('render') ||
      contextLower.includes('display')) {
      return 'medium';
    }

    // Low severity (default)
    return 'low';
  }

  /**
   * Add error to log
   */
  addToLog(errorInfo) {
    this.errorLog.unshift(errorInfo);

    // Keep only the most recent errors
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    // Store in localStorage for persistence (compressed)
    try {
      const compressedLog = this.errorLog.slice(0, 20).map(err => ({
        id: err.id,
        timestamp: err.timestamp,
        message: err.message,
        context: err.context,
        severity: err.severity
      }));
      localStorage.setItem('landgrid-error-log', JSON.stringify(compressedLog));
    } catch (e) {
      console.warn('Failed to store error log in localStorage:', e);
    }
  }

  /**
   * Update error counts for monitoring
   */
  updateErrorCounts(errorInfo) {
    const key = `${errorInfo.context}:${errorInfo.message}`;
    this.errorCounts[key] = (this.errorCounts[key] || 0) + 1;

    // Alert if error repeats frequently
    if (this.errorCounts[key] >= 5) {
      console.warn(`Frequent error detected (${this.errorCounts[key]} occurrences):`, key);
    }
  }

  /**
   * Show user-friendly error message
   */
  showUserError(errorInfo) {
    // Don't show UI errors for low severity issues
    if (errorInfo.severity === 'low') {
      return;
    }

    const userMessage = this.getUserFriendlyMessage(errorInfo);
    const shouldShowDetails = errorInfo.severity === 'critical';

    this.showErrorDialog(userMessage, shouldShowDetails ? errorInfo : null);
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(errorInfo) {
    const context = errorInfo.context.toLowerCase();
    const message = errorInfo.message.toLowerCase();

    // Network errors
    if (context.includes('network') || message.includes('network') || message.includes('fetch')) {
      return 'We\'re having trouble connecting to the internet. Please check your connection and try again.';
    }

    // Map loading errors
    if (context.includes('map') || context.includes('leaflet')) {
      return 'The map is having trouble loading. Please refresh the page and try again.';
    }

    // Data loading errors
    if (context.includes('biome') || context.includes('data')) {
      return 'We\'re having trouble loading the biome data. The map may not display correctly.';
    }

    // UI errors
    if (context.includes('ui') || context.includes('element')) {
      return 'There was a problem with the interface. Please try refreshing the page.';
    }

    // Generic error
    return 'Something went wrong. If the problem continues, please refresh the page.';
  }

  /**
   * Show error dialog
   */
  showErrorDialog(message, errorDetails = null) {
    // Do not show a blocking dialog for low severity issues (e.g., offline/demo mode)
    if (errorDetails && errorDetails.severity === 'low') {
      return;
    }
    const existingDialog = document.getElementById('error-dialog');
    if (existingDialog && !existingDialog.hidden) {
      return; // Don't show multiple dialogs
    }

    const dialog = document.getElementById('error-dialog') || this.createErrorDialog();
    const messageEl = document.getElementById('error-message');
    const retryBtn = document.getElementById('error-retry');
    const dismissBtn = document.getElementById('error-dismiss');

    // Update message
    if (messageEl) {
      messageEl.textContent = message;
    }

    // Set up retry button
    if (retryBtn) {
      retryBtn.onclick = () => {
        dialog.hidden = true;
        window.location.reload();
      };
    }

    // Set up dismiss button
    if (dismissBtn) {
      dismissBtn.onclick = () => {
        dialog.hidden = true;
      };
    }

    // Show dialog
    dialog.hidden = false;

    // Auto-dismiss non-critical errors after 10 seconds
    if (!errorDetails || errorDetails.severity !== 'critical') {
      setTimeout(() => {
        if (!dialog.hidden) {
          dialog.hidden = true;
        }
      }, 10000);
    }
  }

  /**
   * Create error dialog if it doesn't exist
   */
  createErrorDialog() {
    const dialog = document.createElement('div');
    dialog.id = 'error-dialog';
    dialog.className = 'error-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-labelledby', 'error-title');
    dialog.hidden = true;

    dialog.innerHTML = `
      <div class="error-content">
        <h3 id="error-title">Oops! Something went wrong</h3>
        <p id="error-message">We're having trouble loading the map. Please check your connection and try again.</p>
        <div style="display: flex; gap: 8px; justify-content: center; margin-top: 20px;">
          <button id="error-retry" class="error-btn">Try Again</button>
          <button id="error-dismiss" class="error-btn">Dismiss</button>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);
    return dialog;
  }

  /**
   * Report critical errors (placeholder for external error reporting)
   */
  reportCriticalError(errorInfo) {
    // In a production environment, this would send to an error reporting service
    console.warn('Critical error reported:', {
      id: errorInfo.id,
      message: errorInfo.message,
      context: errorInfo.context,
      timestamp: errorInfo.timestamp,
      stack: errorInfo.stack?.substring(0, 500) // Truncate stack trace
    });

    // Store critical errors separately
    try {
      const criticalErrors = JSON.parse(localStorage.getItem('landgrid-critical-errors') || '[]');
      criticalErrors.unshift({
        id: errorInfo.id,
        timestamp: errorInfo.timestamp,
        message: errorInfo.message,
        context: errorInfo.context
      });

      // Keep only last 10 critical errors
      if (criticalErrors.length > 10) {
        criticalErrors.splice(10);
      }

      localStorage.setItem('landgrid-critical-errors', JSON.stringify(criticalErrors));
    } catch (e) {
      console.warn('Failed to store critical error:', e);
    }
  }

  /**
   * Get error statistics
   */
  getErrorStats() {
    const stats = {
      totalErrors: this.errorLog.length,
      criticalErrors: this.errorLog.filter(err => err.severity === 'critical').length,
      highErrors: this.errorLog.filter(err => err.severity === 'high').length,
      mediumErrors: this.errorLog.filter(err => err.severity === 'medium').length,
      lowErrors: this.errorLog.filter(err => err.severity === 'low').length,
      recentErrors: this.errorLog.slice(0, 5),
      frequentErrors: Object.entries(this.errorCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([key, count]) => ({ error: key, count }))
    };

    return stats;
  }

  /**
   * Clear error log
   */
  clearErrorLog() {
    this.errorLog = [];
    this.errorCounts = {};
    localStorage.removeItem('landgrid-error-log');
    localStorage.removeItem('landgrid-critical-errors');
    console.log('Error log cleared');
  }

  /**
   * Export error log
   */
  exportErrorLog() {
    const exportData = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      stats: this.getErrorStats(),
      fullLog: this.errorLog
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `landgrid-error-log-${Date.now()}.json`;
    link.click();

    URL.revokeObjectURL(url);
  }

  /**
   * Load stored error log
   */
  loadStoredErrorLog() {
    try {
      const storedLog = localStorage.getItem('landgrid-error-log');
      if (storedLog) {
        const parsed = JSON.parse(storedLog);
        console.log('Loaded stored error log:', parsed.length, 'errors');
        return parsed;
      }
    } catch (e) {
      console.warn('Failed to load stored error log:', e);
    }
    return [];
  }

  /**
   * Test error handling (for development)
   */
  testErrorHandling() {
    console.log('Testing error handling...');

    // Test different severity levels
    setTimeout(() => this.handleError(new Error('Test low severity error'), 'Test Low'), 100);
    setTimeout(() => this.handleError(new Error('Test UI render error'), 'UI Test'), 200);
    setTimeout(() => this.handleError(new Error('Network request failed'), 'Network Test'), 300);
    setTimeout(() => this.handleError(new Error('Critical initialization failure'), 'Critical Test'), 400);

    console.log('Error handling tests initiated');
  }
}



